import React, { useState, useEffect, useRef } from 'react';
import colors from '../../utils/colors';

const ExtendArrowIcon = () => (
  <svg
    width={12}
    height={12}
    viewBox="0 0 12 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="flex-grow-0 flex-shrink-0 w-3 h-3"
  >
    <path
      d="M3.33333 6C3.23889 6 3.15972 5.96806 3.09583 5.90417C3.03194 5.84028 3 5.76111 3 5.66667V2.33333C3 2.23889 3.03194 2.15972 3.09583 2.09583C3.15972 2.03194 3.23889 2 3.33333 2C3.42778 2 3.50694 2.03194 3.57083 2.09583C3.63472 2.15972 3.66667 2.23889 3.66667 2.33333V5.66667C3.66667 5.76111 3.63472 5.84028 3.57083 5.90417C3.50694 5.96806 3.42778 6 3.33333 6Z"
      fill="#FAF7F5"
    />
    <path
      d="M8.05841 3.66666H5.00008C4.90564 3.66666 4.82647 3.69861 4.76258 3.7625C4.69869 3.82639 4.66675 3.90556 4.66675 4C4.66675 4.09444 4.69869 4.17361 4.76258 4.2375C4.82647 4.30139 4.90564 4.33333 5.00008 4.33333H8.05841L7.10008 5.3C7.03897 5.36111 7.00703 5.43744 7.00425 5.529C7.00147 5.62089 7.03341 5.7 7.10008 5.76667C7.16119 5.82778 7.23897 5.85833 7.33341 5.85833C7.42786 5.85833 7.50564 5.82778 7.56675 5.76667L9.10008 4.23333C9.13341 4.2 9.15703 4.16389 9.17091 4.125C9.1848 4.08611 9.19175 4.04444 9.19175 4C9.19175 3.95556 9.1848 3.91389 9.17091 3.875C9.15703 3.83611 9.13341 3.8 9.10008 3.76667L7.56675 2.23333C7.50564 2.17222 7.42925 2.14167 7.33758 2.14167C7.24591 2.14167 7.16675 2.17222 7.10008 2.23333C7.03341 2.3 7.00008 2.37917 7.00008 2.47083C7.00008 2.5625 7.03341 2.64167 7.10008 2.70833L8.05841 3.66666Z"
      fill="#FAF7F5"
    />
  </svg>
);

const WaveformBar = ({ height, className = "" }) => (
  <div 
    className={`flex-grow-0 flex-shrink-0 w-px rounded-[100px] ${className}`}
    style={{ height: `${height}px` }}
  />
);

const Waveform = ({ 
  audioSrc = "audio.mp3",
  audioFile = null, // Add support for File object
  progress = 0,
  onProgressChange,
  isPlaying = false,
  showTrimHandles = false,
  trimStart = 0,
  trimEnd = 1,
  onTrimChange,
  onTrimDragChange,
  showExtendOverlay = false,
  extendStart = 0.7,
  onExtendChange,
  onExtendDragChange,
  showIntroOverlay = false,
  introLength = 3, // Number of bars (1-8)
  onIntroChange,
  onIntroDragChange,
  showPlusButton = false,
  onPlusClick,
  className = ""
}) => {
  const [waveformData, setWaveformData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [isDraggingTrim, setIsDraggingTrim] = useState(null);
  const [isDraggingExtend, setIsDraggingExtend] = useState(false);
  const [isDraggingIntro, setIsDraggingIntro] = useState(false);
  const waveformRef = useRef(null);
  const [numBars, setNumBars] = useState(140);

  // Generate waveform from audio file
  useEffect(() => {
    const generateWaveform = async () => {
      try {
        setIsLoading(true);
        
        // Ensure we have valid dimensions
        if (numBars <= 0) {
          console.warn('Invalid numBars, waiting for container to resize');
          setIsLoading(false);
          return;
        }
        
        // Add a small delay to ensure audio source is ready
        await new Promise(resolve => setTimeout(resolve, 150));
        
        // Create audio context
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        
        let arrayBuffer;
        
        // Handle File object directly (for uploads) or fetch URL
        if (audioFile && audioFile instanceof File) {
          console.log('Generating waveform from File object');
          arrayBuffer = await audioFile.arrayBuffer();
        } else if (audioSrc && audioSrc !== 'audio.mp3') {
          console.log('Generating waveform from URL:', audioSrc);
          const response = await fetch(audioSrc);
          if (!response.ok) {
            throw new Error(`Failed to fetch audio: ${response.status} ${response.statusText}`);
          }
          arrayBuffer = await response.arrayBuffer();
        } else {
          throw new Error('No valid audio source available');
        }
        
        if (!arrayBuffer || arrayBuffer.byteLength === 0) {
          throw new Error('Empty audio buffer');
        }
        
        // Decode audio data
        const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
        
        // Get channel data (use first channel)
        const channelData = audioBuffer.getChannelData(0);
        
        // Use the calculated number of bars based on container width
        const samplesPerBar = Math.floor(channelData.length / numBars);
        
        const waveform = [];
        
        for (let i = 0; i < numBars; i++) {
          let sum = 0;
          const start = i * samplesPerBar;
          const end = Math.min(start + samplesPerBar, channelData.length);
          
          // Calculate RMS (Root Mean Square) for this segment
          for (let j = start; j < end; j++) {
            sum += channelData[j] * channelData[j];
          }
          
          const rms = Math.sqrt(sum / (end - start));
          
          // Scale to our desired height range with increased sensitivity
          const height = Math.max(1, Math.min(48, Math.floor(rms * 520)));
          
          waveform.push(height);
        }
        
        console.log('Waveform generated successfully:', waveform.length, 'bars');
        setWaveformData(waveform);
        setIsLoading(false);
        
        // Clean up audio context
        audioContext.close();
      } catch (error) {
        console.error('Error generating waveform:', error);
        
        // Always provide fallback data to ensure something displays
        const fallbackData = Array.from({ length: Math.max(numBars, 100) }, () => 
          Math.floor(Math.random() * 48) + 8
        );
        console.log('Using fallback waveform data:', fallbackData.length, 'bars');
        setWaveformData(fallbackData);
        setIsLoading(false);
      }
    };

    // Only generate if we have a valid audio source and container dimensions
    if ((audioFile && audioFile instanceof File) || (audioSrc && audioSrc !== 'audio.mp3')) {
      generateWaveform();
    } else {
      // Show placeholder bars while waiting for audio
      const placeholderData = Array.from({ length: Math.max(numBars, 100) }, () => 
        Math.floor(Math.random() * 20) + 5
      );
      setWaveformData(placeholderData);
      setIsLoading(false);
    }
  }, [audioSrc, audioFile, numBars]);

  // Calculate number of bars based on container width
  useEffect(() => {
    const calculateBars = () => {
      if (waveformRef.current) {
        const containerWidth = waveformRef.current.offsetWidth;
        const paddingWidth = 24; // Keep consistent padding
        const availableWidth = containerWidth - paddingWidth;
        const barWidth = 1; // 1px bar width
        const gapWidth = 2; // 2px gap
        const barAndGapWidth = barWidth + gapWidth;
        const calculatedBars = Math.floor(availableWidth / barAndGapWidth);
        setNumBars(Math.max(60, calculatedBars)); // Use more bars for smoother intro mode
      }
    };

    calculateBars();
    window.addEventListener('resize', calculateBars);
    return () => window.removeEventListener('resize', calculateBars);
  }, []);

  // Drag functionality for progress
  const handleMouseDown = (e) => {
    setIsDragging(true);
    handleSeek(e);
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      handleSeek(e);
    } else if (isDraggingTrim) {
      handleTrimSeek(e);
    } else if (isDraggingExtend) {
      handleExtendSeek(e);
    } else if (isDraggingIntro) {
      handleIntroSeek(e);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    if (isDraggingTrim) {
      onTrimDragChange?.(null);
    }
    setIsDraggingTrim(null);
    if (isDraggingExtend) {
      onExtendDragChange?.(false);
    }
    setIsDraggingExtend(false);
    if (isDraggingIntro) {
      onIntroDragChange?.(false);
    }
    setIsDraggingIntro(false);
  };

  const handleSeek = (e) => {
    const container = waveformRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const paddingLeft = 12; // 0.75rem in pixels
    const paddingRight = 12;
    const availableWidth = rect.width - paddingLeft - paddingRight;
    const clickX = e.clientX - rect.left - paddingLeft;
    
    // Clamp the position within bounds
    const clampedX = Math.max(0, Math.min(clickX, availableWidth));
    let newProgress = clampedX / availableWidth;
    
    // In extend mode, constrain progress within the extend area (extendStart to 1)
    if (showExtendOverlay) {
      newProgress = Math.max(extendStart, Math.min(1, newProgress));
    }
    
    onProgressChange?.(newProgress);
  };

  // Trim handle functionality
  const handleTrimMouseDown = (type, e) => {
    e.stopPropagation();
    setIsDraggingTrim(type);
    onTrimDragChange?.(type);
  };

  const handleTrimSeek = (e) => {
    const container = waveformRef.current;
    if (!container || !isDraggingTrim) return;

    const rect = container.getBoundingClientRect();
    const paddingLeft = 12; // 0.75rem in pixels
    const paddingRight = 12;
    const availableWidth = rect.width - paddingLeft - paddingRight;
    const clickX = e.clientX - rect.left - paddingLeft;
    
    // Clamp the position within bounds
    const clampedX = Math.max(0, Math.min(clickX, availableWidth));
    const newPosition = clampedX / availableWidth;
    
    if (isDraggingTrim === 'start') {
      onTrimChange?.(newPosition, trimEnd);
    } else if (isDraggingTrim === 'end') {
      onTrimChange?.(trimStart, newPosition);
    }
  };

  // Extend handle functionality
  const handleExtendMouseDown = (e) => {
    e.stopPropagation();
    setIsDraggingExtend(true);
    onExtendDragChange?.(true);
  };

  const handleExtendSeek = (e) => {
    const container = waveformRef.current;
    if (!container || !isDraggingExtend) return;

    const rect = container.getBoundingClientRect();
    const paddingLeft = 12; // 0.75rem in pixels
    const paddingRight = 12;
    const availableWidth = rect.width - paddingLeft - paddingRight;
    const clickX = e.clientX - rect.left - paddingLeft;
    
    // Clamp the position within bounds
    const clampedX = Math.max(0, Math.min(clickX, availableWidth));
    const newPosition = clampedX / availableWidth;
    
    // Only allow positions between 0 and 1, and make sure it's less than 1
    const clampedPosition = Math.max(0, Math.min(0.95, newPosition));
    onExtendChange?.(clampedPosition);
  };

  // Intro handle functionality
  const handleIntroMouseDown = (e) => {
    e.stopPropagation();
    setIsDraggingIntro(true);
    onIntroDragChange?.(true);
  };

  const handleIntroSeek = (e) => {
    const container = waveformRef.current;
    if (!container || !isDraggingIntro) return;

    const rect = container.getBoundingClientRect();
    const paddingLeft = 12; // 0.75rem in pixels
    const paddingRight = 12;
    const availableWidth = rect.width - paddingLeft - paddingRight;
    const clickX = e.clientX - rect.left - paddingLeft;
    
    // Clamp the position within bounds
    const clampedX = Math.max(0, Math.min(clickX, availableWidth));
    const newPosition = clampedX / availableWidth;
    
    // Calculate intro length based on position (each bar is 20px, max 8 bars = 160px)
    const barWidth = 20;
    const maxBars = 8;
    const maxWidth = maxBars * barWidth;
    const pixelPosition = newPosition * availableWidth;
    const bars = Math.max(1, Math.min(maxBars, Math.round(pixelPosition / barWidth)));
    
    onIntroChange?.(bars);
  };

  // Global mouse events for dragging
  useEffect(() => {
    if (isDragging || isDraggingTrim || isDraggingExtend || isDraggingIntro) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, isDraggingTrim, isDraggingExtend, isDraggingIntro]);

  return (
    <div className={`h-20 relative border border-white/10 rounded-xl ${className}`}>
      <div 
        ref={waveformRef} 
        className="w-full h-12 absolute left-0 top-4 overflow-hidden px-3 cursor-pointer"
        onMouseDown={!showTrimHandles ? handleMouseDown : undefined}
      >
        <div className="flex justify-start items-center h-12 w-full overflow-hidden gap-0.5">
          {isLoading ? (
            // Loading placeholder with better animation
            Array.from({ length: Math.min(numBars, 100) }, (_, i) => (
              <div 
                key={i}
                className="w-px rounded-[100px] bg-white/30"
                style={{ 
                  height: `${Math.sin(i * 0.3) * 15 + 20}px`,
                  animation: `pulse 1.5s ease-in-out ${i * 0.05}s infinite alternate`
                }}
              />
            ))
          ) : !showIntroOverlay ? (
            // Actual waveform data - hidden in intro mode
            waveformData.map((height, index) => {
              let barColor = colors.text.inactive; // Default unplayed
              
              if (showTrimHandles || showExtendOverlay) {
                // Trim mode and extend mode - use single white color
                barColor = "#faf7f5";
              } else {
                // Normal playback coloring
                const barProgress = index / waveformData.length;
                barColor = barProgress < progress ? colors.accent : colors.text.inactive;
              }

              return (
                <div
                  key={index}
                  className="w-px rounded-[100px]"
                  style={{ 
                    height: `${height}px`,
                    backgroundColor: barColor 
                  }}
                />
              );
            })
          ) : null}
        </div>
      </div>

      {/* Progress Line - only show when not in extend, trim, or intro mode */}
      {!isLoading && !showTrimHandles && !showExtendOverlay && !showIntroOverlay && (
        <>
          {/* Invisible drag area */}
          <div 
            className={`absolute top-0 bottom-0 w-3 cursor-grab ${isDragging ? 'cursor-grabbing transition-none' : 'transition-all duration-100 ease-linear'}`}
            style={{ 
              left: `calc(0.75rem + ${progress} * (100% - 1.5rem) - 6px)`
            }}
            onMouseDown={handleMouseDown}
          />
          {/* Visible progress line */}
          <div 
            className={`absolute top-0 bottom-0 w-0.5 bg-white pointer-events-none ${isDragging ? 'transition-none' : 'transition-all duration-100 ease-linear'}`}
            style={{ 
              left: `calc(0.75rem + ${progress} * (100% - 1.5rem))`
            }}
          />
        </>
      )}

      {/* Trim Selection UI */}
      {showTrimHandles && !isLoading && (
        <>
          {/* Trim selection overlay with border */}
          <div 
            className="absolute top-0 bottom-0 overflow-hidden"
            style={{ 
              left: `calc(0.75rem + ${trimStart} * (100% - 1.5rem))`,
              width: `calc(${(trimEnd - trimStart)} * (100% - 1.5rem))`,
              border: `1px solid ${colors.accent}`
            }}
          >
            {/* Semi-transparent overlay */}
            <div 
              className="absolute inset-0"
              style={{ backgroundColor: `${colors.accent}80` }}
            />
            
          </div>

          {/* Left trim handle */}
          <div 
            className="absolute top-0 bottom-0 w-3 cursor-grab flex items-center justify-center rounded-l-md z-20"
            style={{ 
              left: `calc(0.75rem + ${trimStart} * (100% - 1.5rem) - 12px)`,
              backgroundColor: colors.accent
            }}
            onMouseDown={(e) => handleTrimMouseDown('start', e)}
          >
          </div>
          
          {/* Right trim handle */}
          <div 
            className="absolute top-0 bottom-0 w-3 cursor-grab flex items-center justify-center rounded-r-md z-20"
            style={{ 
              left: `calc(0.75rem + ${trimEnd} * (100% - 1.5rem))`,
              backgroundColor: colors.accent
            }}
            onMouseDown={(e) => handleTrimMouseDown('end', e)}
          >
          </div>

          {/* Progress line within trim selection - hidden when dragging trim handles */}
          {!isDraggingTrim && (() => {
            // Calculate progress position directly as a percentage of the full waveform
            // When progress equals trimStart, position should be at trimStart
            // When progress equals trimEnd, position should be at trimEnd
            const clampedProgress = Math.max(trimStart, Math.min(trimEnd, progress));
            
            return (
              <>
                {/* Invisible drag area for trim progress */}
                <div 
                  className={`absolute top-0 bottom-0 w-3 cursor-grab ${isDragging ? 'cursor-grabbing transition-none' : 'transition-all duration-100 ease-linear'}`}
                  style={{ 
                    left: `calc(0.75rem + ${clampedProgress} * (100% - 1.5rem) - 6px)`
                  }}
                  onMouseDown={handleMouseDown}
                />
                {/* Visible progress line */}
                <div 
                  className={`absolute top-0 bottom-0 w-0.5 bg-white pointer-events-none z-10 ${isDragging ? 'transition-none' : 'transition-all duration-100 ease-linear'}`}
                  style={{ 
                    left: `calc(0.75rem + ${clampedProgress} * (100% - 1.5rem))`
                  }}
                />
              </>
            );
          })()}
        </>
      )}

      {/* Extend Overlay */}
      {showExtendOverlay && !isLoading && (
        <>
          {/* Extend selection overlay - from extend point to end */}
          <div 
            className="absolute right-0 overflow-hidden z-10"
            style={{ 
              top: '-1px',
              height: '80px', // Match the full container height
              left: `calc(0.75rem + ${extendStart} * (100% - 1.5rem))`,
              background: `${colors.accent}80`,
              border: `1px solid ${colors.accent}`,
              borderTopLeftRadius: 0,
              borderBottomLeftRadius: 0,
              borderTopRightRadius: '12px',
              borderBottomRightRadius: '12px'
            }}
          >
          </div>

          {/* Extend Left Handle - positioned above waveform */}
          <div 
            className="absolute w-3 cursor-ew-resize flex flex-col items-center justify-center z-20"
            style={{ 
              top: '-1px',
              height: '80px', // Match the full container height
              left: `calc(0.75rem + ${extendStart} * (100% - 1.5rem) - 6px)`,
              borderTopLeftRadius: '4px',
              borderBottomLeftRadius: '4px',
              backgroundColor: colors.accent
            }}
            onMouseDown={handleExtendMouseDown}
          >

          </div>

          {/* Progress line within extend selection - hidden when dragging extend handle */}
          {!isDraggingExtend && (() => {
            // Calculate progress position clamped within extend area (extendStart to 1)
            const clampedProgress = Math.max(extendStart, Math.min(1, progress));
            
            return (
              <>
                {/* Invisible drag area for extend progress */}
                <div 
                  className={`absolute top-0 bottom-0 w-3 cursor-grab ${isDragging ? 'cursor-grabbing transition-none' : 'transition-all duration-100 ease-linear'}`}
                  style={{ 
                    left: `calc(0.75rem + ${clampedProgress} * (100% - 1.5rem) - 6px)`
                  }}
                  onMouseDown={handleMouseDown}
                />
                {/* Visible progress line */}
                <div 
                  className={`absolute top-0 bottom-0 w-0.5 bg-white pointer-events-none z-10 ${isDragging ? 'transition-none' : 'transition-all duration-100 ease-linear'}`}
                  style={{ 
                    left: `calc(0.75rem + ${clampedProgress} * (100% - 1.5rem))`
                  }}
                />
              </>
            );
          })()}
        </>
      )}

      {/* Intro Overlay */}
      {showIntroOverlay && !isLoading && (
        <>
          {/* Background waveform with reduced opacity - positioned to the right of intro selection */}
          <div 
            className="absolute flex items-center justify-start opacity-50 overflow-hidden"
            style={{ 
              top: '16px', // Center vertically like in reference (48px height centered in 80px container)
              height: '48px', // Match the waveform container height
              left: `calc(${introLength * 20 + 12}px)`, // Position right after intro selection + handle
              right: '12px', // Right padding to match container
              gap: '2px' // Standard gap between bars
            }}
          >
            {(() => {
              // Calculate how many bars can fit in the available space
              const containerWidth = waveformRef.current?.offsetWidth || 400;
              const introWidth = introLength * 20 + 12; // intro selection + handle
              const availableWidth = containerWidth - introWidth - 12; // minus right padding
              const barAndGapWidth = 3; // 1px bar + 2px gap
              const maxBars = Math.floor(availableWidth / barAndGapWidth);
              const barsToShow = Math.max(10, Math.min(maxBars, waveformData.length));
              
              return waveformData.slice(0, barsToShow).map((height, index) => (
                <div
                  key={index}
                  className="w-px rounded-[100px] bg-[#faf7f5] flex-shrink-0"
                  style={{ height: `${height}px` }}
                />
              ));
            })()}
          </div>

          {/* Intro selection area - from start to intro length */}
          <div 
            className="absolute left-0 overflow-hidden z-10"
            style={{ 
              top: '-1px',
              height: '80px', // Match the full container height
              left: `0px`, // Align to the very left edge
              width: `${introLength * 20}px`, // Each bar is 20px
              background: `${colors.accent}80`,
              border: `1px solid ${colors.accent}`,
              borderTopLeftRadius: '12px',
              borderBottomLeftRadius: '12px',
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0
            }}
          >
            {/* Dotted bar lines */}
            {Array.from({ length: introLength }, (_, i) => (
              <div
                key={i}
                className="absolute top-0 bottom-0 w-0.5"
                style={{
                  left: `${(i + 1) * 20 - 1}px`, // Position at end of each bar
                  background: `repeating-linear-gradient(
                    to bottom,
                    ${colors.accent} 0px,
                    ${colors.accent} 4px,
                    transparent 4px,
                    transparent 8px
                  )`
                }}
              />
            ))}
          </div>

          {/* Intro Right Handle */}
          <div 
            className="absolute w-3 cursor-ew-resize flex flex-col items-center justify-center z-20"
            style={{ 
              top: '-1px',
              height: '80px', // Match the full container height
              left: `${introLength * 20}px`, // Position right after intro selection
              borderTopRightRadius: '4px',
              borderBottomRightRadius: '4px',
              backgroundColor: colors.accent
            }}
            onMouseDown={handleIntroMouseDown}
          >
          </div>

          {/* Progress line for intro mode - positioned in the waveform area to the right */}
          {(() => {
            // Calculate progress position in the waveform area (after intro selection)
            // The progress should be relative to the waveform area, not the entire container
            
            return (
              <>
                {/* Invisible drag area for intro progress */}
                <div 
                  className={`absolute top-0 bottom-0 w-3 cursor-grab ${isDragging ? 'cursor-grabbing transition-none' : 'transition-all duration-100 ease-linear'}`}
                  style={{ 
                    left: `calc(${introLength * 20 + 12}px + ${progress} * (100% - ${introLength * 20 + 12}px - 0.75rem) - 6px)`
                  }}
                  onMouseDown={handleMouseDown}
                />
                {/* Visible progress line */}
                <div 
                  className={`absolute top-0 bottom-0 w-0.5 bg-white pointer-events-none z-10 ${isDragging ? 'transition-none' : 'transition-all duration-100 ease-linear'}`}
                  style={{ 
                    left: `calc(${introLength * 20 + 12}px + ${progress} * (100% - ${introLength * 20 + 12}px - 0.75rem))`
                  }}
                />
              </>
            );
          })()}
        </>
      )}
      
      {/* Plus Button Overlay */}
      {showPlusButton && (
        <button 
          className="flex justify-center items-center w-10 h-10 absolute right-2 bottom-2 gap-1 px-4 rounded-full bg-white/[0.04] backdrop-blur-[50px] hover:bg-white/[0.08] transition-colors cursor-pointer z-30"
          onClick={onPlusClick}
        >
          <svg
            width={16}
            height={16}
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="flex-grow-0 flex-shrink-0 w-4 h-4 relative"
            preserveAspectRatio="xMidYMid meet"
          >
            <path
              d="M8 2C7.52661 2 7.14286 2.38376 7.14286 2.85714V7.14286L2.85714 7.14286C2.38376 7.14286 2 7.52661 2 8C2 8.47339 2.38376 8.85714 2.85714 8.85714H7.14286V13.1429C7.14286 13.6162 7.52661 14 8 14C8.47339 14 8.85714 13.6162 8.85714 13.1429V8.85714H13.1429C13.6162 8.85714 14 8.47339 14 8C14 7.52661 13.6162 7.14286 13.1429 7.14286L8.85714 7.14286V2.85714C8.85714 2.38376 8.47339 2 8 2Z"
              fill="#FAF7F5"
            />
          </svg>
        </button>
      )}
    </div>
  );
};

export default Waveform;